import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Inject, Injectable } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { plainToClass } from 'class-transformer';
import { catchError, debounceTime, first, forkJoin, map, mergeMap, Observable, of, ReplaySubject, tap } from 'rxjs';

import { MediaService } from '@eiq/files';
import { Media, MediaView } from '@eiq/files/models';
import { ProjectInvite } from '@eiq/panel-members/data';
import { DataVariable, ProjectStatus } from '@eiq/shared/data';
import { ToQueryParams } from '@eiq/shared/utils';
import { User, UserBasic } from '@eiq/users/data';

import { Comment, CommentType } from '@empathy/comments/data';
import { AIPrompt } from '@empathy/global/audio-chat/models';
import { ResearchType } from '@empathy/global/data';
import { GetTimeDifference, ToOrdinalNumber } from '@empathy/global/functions';
import { CalculateDateDifferenceAsString } from '@empathy/global/utils';
import {
  AIPromptCore,
  CarouselType,
  ChoiceTypeResult,
  IFollowUp,
  PageModel,
  Question,
  QuestionOption,
  QuestionType,
  RatingType,
  SurveyModel
} from '@empathy/survey/base';
import { createTurnId } from '@empathy/survey/base/turn-id';
import { NotepadIdPrefix, NotepadModel, NotepadQuestion } from '@empathy/survey-notepad';

import { AiInterviewService } from './ai-interview.service';
import { GenieService } from './genie.service';
import { EnvironmentConfig } from '../config/environment.config';
import { AiInterviewContentRequest } from '../models/ai-interview/ai-interview-content-request.interface';
import { AiInterviewQuestion } from '../models/ai-interview/ai-interview-question.model';
import { AIInterviewTone } from '../models/ai-interview/ai-interview-tone.enum';
import { AIQuestion, AIResponseModel } from '../models/ai-interview/ai-response.model';
import { AISurveyType } from '../models/ai-interview/ai-survey-type.enum';
import { ProjectAiInterviewContent } from '../models/ai-interview/project-ai-interview-content.model';
import { AiResearchAnalytics } from '../models/ai-research-analytics.interface';
import { ResearchAudienceTypeEnum } from '../models/audience/audience-type.enum';
import { ProjectClient } from '../models/client.model';
import { FetchRequest } from '../models/dashboard/fetch-request.interface';
import { Dropouts } from '../models/dropouts.interface';
import { EditResearchDetails } from '../models/edit-research-details.interface';
import { SurveyFeedback } from '../models/feedback/survey-feedback.interface';
import { FlagType } from '../models/flag-type.interface';
import { GridProjects } from '../models/grid-project.model';
import { IEditMainResearchDetails } from '../models/main-research-details.interface';
import { ProjectParticipant } from '../models/participants.model';
import { PrizeDrawEntry } from '../models/prize-draw-entry.interface';
import { ProjectClass } from '../models/project-class.enum';
import { ProjectResponse } from '../models/project-responses.interface';
import { ProjectStatistics } from '../models/project-statistics.interface';
import { Project } from '../models/project.model';
import { RegenerateHiddenQuestionsResponse } from '../models/regenerate-hidden-questions-response.interface';
import { ResearchDetails } from '../models/research-details.model';
import { ResearchMethodSelectItem } from '../models/research-method-select-item.interface';
import { ResearchStatus } from '../models/research-status.enum';
import { InviteUserToCollaborateOnSurvey } from '../models/survey/invite-user-to-collaborate-on-survey.model';
import { SurveyAnswer } from '../models/survey-answer.model';
import { SurveyAnswersCsvExportRequest } from '../models/survey-answers-csv-export-request.interface';
import { SurveyResearchAnalytics } from '../models/survey-research-analytics.interface';
import { ProjectSelectListItem } from '../models/project-select-list-item.interface';
import { ProjectTerminations } from '../models/termination.interface';

@Injectable({
  providedIn: 'root'
})
export class ProjectsAdminService {
  aiSurvey$ = new ReplaySubject<AIResponseModel>(1);
  answers: SurveyAnswer[];
  messages: { type: 'ai' | 'user'; text: string | number }[] = [];

  private aiInterviewService = inject(AiInterviewService);
  private genieService = inject(GenieService);
  private mediaService = inject(MediaService);
  private fb = inject(FormBuilder);
  private http = inject(HttpClient);

  constructor(@Inject('environment') private environment: EnvironmentConfig) {
    this.aiSurvey$.next(null);
  }

  exists(projectName: string): Observable<boolean> {
    return this.http.post<boolean>(`${this.environment.endPoint}project/name-exists`, { projectName: projectName }).pipe(catchError(() => of(true)));
  }

  completeClientBriefSettings(projectId: string, methodId: string, clientBriefSettingsId: string): Observable<boolean> {
    return this.http.post<boolean>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/intake/${clientBriefSettingsId}/complete`, null);
  }

  confirmSurveyResponse(projectId: string, methodId: string, answerId: string): Observable<boolean> {
    return this.http.post(`${this.environment.endPoint}project/${projectId}/research/${methodId}/answers/${answerId}/confirm`, null).pipe(
      map(() => true),
      catchError((error) => of(error))
    );
  }

  convertNotepadToEditor(surveyJson: string, projectId: string, researchMethodId: string): Observable<boolean | Project> {
    const url = `${this.environment.endPoint}project/${projectId}/research/${researchMethodId}/survey/editor`;

    return this.http.put<any>(url, { surveyJson });
  }

  delete(projectId: string): Observable<boolean | HttpErrorResponse> {
    return this.http.delete<boolean>(`${this.environment.endPoint}project/${projectId}`).pipe(
      map(() => true),
      catchError((error) => of(error))
    );
  }

  closeProject(projectId: string, methodId: string): Observable<boolean | HttpErrorResponse> {
    return this.http.put(`${this.environment.endPoint}project/${projectId}/research/${methodId}/close`, null).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  reopenProject(projectId: string, methodId: string): Observable<boolean | HttpErrorResponse> {
    return this.http.patch(`${this.environment.endPoint}project/${projectId}/research/${methodId}/reopen`, null).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  exportAs(projectId: string, format: string): Observable<unknown> {
    return this.http.get<DataVariable[]>(`${this.environment.endPoint}project/${projectId}/export/${format}`);
  }

  exportSurveyAnswersToCsv(projectId: string, methodId: string, request: SurveyAnswersCsvExportRequest): Observable<File> {
    return this.http.post<File>(`${this.environment.surveyEndPoint}analytics/csv/${projectId}/${methodId}`, request, { responseType: 'blob' as 'json' });
  }

  exportSurveyAnswersToSPSS(
    projectId: string,
    methodId: string,
    isTestDataInd: boolean,
    status?: number,
    fromDate?: string,
    toDate?: string
  ): Observable<File> {
    return this.http.post<File>(
      `${this.environment.endPoint}project/${projectId}/research/${methodId}/survey/export-spss`,
      { isTestDataInd, status, fromDate, toDate },
      { responseType: 'blob' as 'json' }
    );
  }

  regenerateHiddenQuestions(projectId: string, methodId: string): Observable<RegenerateHiddenQuestionsResponse | null> {
    return this.http
      .post<RegenerateHiddenQuestionsResponse>(`${this.environment.surveyEndPoint}hidden-questions/regenerate/${projectId}/${methodId}`, null)
      .pipe(catchError(() => of(null)));
  }

  bulkUpdateParticipantStatus(
    projectId: string,
    methodId: string,
    answerIds: string[],
    status: number,
    reason: string = '',
    flagType?: string
  ): Observable<boolean> {
    const body: Record<string, unknown> = { answerIds, status, reason };

    if (flagType) {
      body.flagType = flagType;
    }

    return this.http.post(`${this.environment.endPoint}project/${projectId}/research/${methodId}/answers/bulk-status-update`, body).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  getFlagTypes(): Observable<FlagType[]> {
    return this.http.get<FlagType[]>(`${this.environment.endPoint}configs/flag-types`).pipe(catchError(() => of([])));
  }

  get(projectId: string): Observable<Project> {
    return this.http.get<Project>(`${this.environment.endPoint}project/${projectId}`).pipe(catchError(() => of(null)));
  }

  editProjectBrief(projectId: string, brief: string): Observable<any> {
    return this.http.put<any>(`${this.environment.endPoint}project/${projectId}`, { brief: brief }).pipe(catchError(() => of(null)));
  }

  editLiveResearch(projectId: string, methodId: string): Observable<string> {
    return this.http.put<{ newResearchMethodId: string }>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/edit`, {}).pipe(
      map((res) => res.newResearchMethodId),
      catchError(() => of(null))
    );
  }

  discardEditLiveResearch(projectId: string, methodId: string): Observable<boolean> {
    return this.http.delete<boolean>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/edit`).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  getAiResearchAnalytics(projectId: string, researchMethodId: string): Observable<AiResearchAnalytics> {
    return forkJoin([
      this.aiInterviewService.getAiInterviewPendingTranscripts(projectId, researchMethodId),
      this.aiInterviewService.getAiSummary(projectId, researchMethodId)
    ]).pipe(map(([pendingTranscripts, aiSummary]) => ({ pendingTranscripts, aiSummary })));
  }

  fetchProjects(request: FetchRequest): Observable<GridProjects> {
    return this.http
      .get<{ totalNumberResults: number; results: Project[] }>(`${this.environment.endPoint}project?${ToQueryParams(request)}`)
      .pipe(map((response) => new GridProjects(response.totalNumberResults, response.results)));
  }

  getResearchMethodSelectList(researchType?: ResearchType): Observable<ResearchMethodSelectItem[] | null> {
    const params = researchType != null ? { researchType } : {};
    return this.http.get<ResearchMethodSelectItem[]>(`${this.environment.endPoint}research/select-list`, { params }).pipe(catchError(() => of(null)));
  }

  getAnswers(projectId: string, methodId: string, refresh: boolean = false): Observable<SurveyAnswer[]> {
    if (!refresh && this.answers) {
      return of(this.answers);
    }

    return this.http.get<SurveyAnswer[]>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/survey/answers`).pipe(
      map((answers) =>
        answers.map((answer) => ({
          ...answer,
          timeUsed: CalculateDateDifferenceAsString(new Date(answer.answerStartDateTime), new Date(answer.answerCompletedDateTime))
        }))
      ),
      tap((answers) => (this.answers = answers))
    );
  }

  getAnswerDetails(projectId: string, methodId: string, answerId: string): Observable<SurveyAnswer> {
    return this.http.get<SurveyAnswer>(`${this.environment.surveyEndPoint}answers/${projectId}/${methodId}/${answerId}`);
  }

  deleteTestAnswers(projectId: string, methodId: string): Observable<boolean> {
    return this.http
      .delete<boolean>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/survey/answer/test-responses`)
      .pipe(catchError(() => of(false)));
  }

  getByStatus(status: string, request: FetchRequest): Observable<{ results: Project[]; totalNumberResults: number }> {
    return this.http
      .get<{ results: Project[]; totalNumberResults: number }>(`${this.environment.endPoint}project?status=${status}&?${ToQueryParams(request)}`)
      .pipe(catchError(() => of(null)));
  }

  getProjectSelectList(status: string, excludeProjectIds: string[]): Observable<ProjectSelectListItem[]> {
    return this.http
      .get<ProjectSelectListItem[]>(`${this.environment.endPoint}project/select-list`, { params: { status, excludeProjectIds } })
      .pipe(catchError(() => of([])));
  }

  getClientById(id: string): Observable<ProjectClient> {
    return this.http.get<ProjectClient>(`${this.environment.endPoint}clients/${id}`).pipe(catchError(() => of(null)));
  }

  getComments(id: string, typeId: CommentType): Observable<Comment[]> {
    return this.http.get<Comment[]>(`${this.environment.endPoint}comments?id=${id}&typeId=${typeId}`).pipe(
      map((comments) => comments?.map((comment) => this.mapToGenericComment(comment, comments)).filter((c) => !c.replyToCommentId)),
      catchError(() => of([]))
    );
  }

  getDropouts(projectId: string, methodId: string): Observable<Dropouts> {
    return this.http
      .get<Dropouts>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/statistics/dropouts`)
      .pipe(catchError(() => of(null)));
  }

  getGeneratedQuestions(brief: string, category: AISurveyType, chatReference: string): Observable<boolean> {
    const urlRequest = !chatReference
      ? this.http.post<AIResponseModel>(`${this.environment.endPoint}genie/survey/generate`, { brief, category })
      : this.http.post<AIResponseModel>(`${this.environment.endPoint}genie/survey/refine`, { chatReference, category, refineQuery: brief });

    return urlRequest.pipe(
      tap((generatedQuestions) => generatedQuestions.items.forEach((q) => (q.selected = true))),
      tap((generatedQuestions) => this.aiSurvey$.next(generatedQuestions)),
      map(() => true),
      catchError(() => of(null))
    );
  }

  generateNewComment(id: string, questionId: string, type: CommentType): Comment {
    return new Comment(id, `#${questionId}`, new Date(), '', type);
  }

  getParticipants(projectId: string, methodId: string): Observable<ProjectParticipant[]> {
    return this.http
      .get<ProjectParticipant[]>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/statistics/participants`)
      .pipe(catchError(() => of(null)));
  }

  getStatistics(projectId: string, methodId: string): Observable<ProjectStatistics> {
    return this.http
      .get<ProjectStatistics>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/statistics`)
      .pipe(catchError(() => of(null)));
  }

  getSurveyResearchAnalytics(projectId: string, researchMethodId: string): Observable<SurveyResearchAnalytics> {
    return forkJoin([
      this.getStatistics(projectId, researchMethodId),
      this.getTerminates(projectId, researchMethodId),
      this.getDropouts(projectId, researchMethodId),
      this.getParticipants(projectId, researchMethodId),
      this.genieService.getAiSummary(projectId, researchMethodId)
    ]).pipe(
      map(([statistics, terminations, dropouts, participants, surveyAiSummary]) => ({
        statistics,
        terminations,
        dropouts,
        participants,
        surveyAiSummary
      }))
    );
  }

  getTerminates(projectId: string, methodId: string): Observable<ProjectTerminations> {
    return this.http
      .get<ProjectTerminations>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/statistics/terminate`)
      .pipe(catchError(() => of(null)));
  }

  initDiscussionTopicReplyForm(): FormGroup {
    return this.fb.group({
      reply: ['', Validators.required]
    });
  }

  initProjectAiInterviewContentForm(): FormGroup {
    return this.fb.group({
      companyName: ['', Validators.required],
      interviewerName: ['', [Validators.required, Validators.maxLength(50)]],
      interviewTone: [AIInterviewTone.Friendly, Validators.required],
      context: ['', [Validators.required, Validators.maxLength(1400)]],
      openingMessage: ['', Validators.required],
      researchObjective: ['', [Validators.required, Validators.maxLength(700)]],
      targetAudience: ['', [Validators.required, Validators.maxLength(100)]],
      targetCompletes: [0, [Validators.required, Validators.min(1)]]
    });
  }

  initProjectDiscussionForumContentForm(): FormGroup {
    return this.fb.group({
      forumTitle: ['', Validators.required],
      forumDescription: ['', [Validators.maxLength(1000)]],
      openingMessage: ['', [Validators.maxLength(1400)]],
      guidelines: ['', [Validators.maxLength(1400)]],
      discussionForumTopics: this.fb.array([])
    });
  }

  initProjectDiscussionForumTopicContentForm(): FormGroup {
    return this.fb.group({
      id: [''],
      reference: [''],
      discussionTitle: ['', Validators.required],
      discussionDescription: ['', [Validators.maxLength(1000)]],
      openingMessage: ['', [Validators.maxLength(1400)]],
      guidelines: ['', [Validators.maxLength(1400)]],
      individualForumInd: [false]
    });
  }

  initProjectForm(): FormGroup {
    return this.fb.group({
      id: [],
      clientId: [],
      brief: ['', Validators.maxLength(1000)],
      externalProjectId: [],
      methods: this.fb.array([]),
      name: ['', [Validators.required, Validators.maxLength(150)], [this.validateProjectName()]],
      projectTypeId: [ProjectClass.Research],
      projectStatus: [ProjectStatus.Prelaunch],
      clientIntakeFormInd: [false]
    });
  }

  initDuplicateProjectControl(): FormControl {
    return new FormControl('', [Validators.required, Validators.maxLength(150)], [this.validateProjectName()]);
  }

  initResearchDetails(researchDetails?: ResearchDetails): FormGroup {
    const reseachDetailsGroup = this.fb.group({
      title: [''],
      image: [1],
      description: [''],
      shortDescription: [''],
      targetCompletes: [0],
      lengthOfInterview: [0],
      incidenceRate: [0, [Validators.required, Validators.min(0)]],
      startDate: [],
      endDate: [],
      raffleTicketsAvailable: [0],
      pointsAvailable: [0],
      allowAnoymousInd: [false],
      allowMultipleInd: [false],
      prizeDrawEntryActiveInd: [false],
      prizeDetails: ['']
    });

    if (researchDetails) {
      reseachDetailsGroup.patchValue(researchDetails);

      if (!researchDetails.prizeDrawEntryActiveInd) {
        reseachDetailsGroup.get('prizeDetails').disable();
      }
    }

    return reseachDetailsGroup;
  }

  initResearchForm(): FormGroup {
    return this.fb.group({
      researchType: [],
      status: [ResearchStatus.Draft],
      audience: this.fb.group({
        audienceType: [ResearchAudienceTypeEnum.PANEL_RECRUITMENT]
      }),
      countryCode: ['IE'], // TODO: SET ON WIZARD
      language: ['EN'], // TODO: SET ON WIZARD
      researchDetails: this.initResearchDetails()
    });
  }

  inviteUserToCollaborate(projectId: string, methodId: string, request: InviteUserToCollaborateOnSurvey): Observable<UserBasic> {
    return this.http
      .post<User>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/collaborators/user`, request)
      .pipe(catchError((error) => of(error)));
  }

  launchResearchMethod(projectId: string, methodId: string): Observable<string | null> {
    return this.http.put<{ researchMethodId: string }>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/launch`, null).pipe(
      map((result) => result.researchMethodId),
      catchError(() => of(null))
    );
  }

  postComment(comment: Comment, researchMethodId: string): Observable<Comment> {
    const newCommentServer = this.mapCommentToServerComment(comment, researchMethodId);

    return this.http.post<Comment>(`${this.environment.endPoint}comments`, newCommentServer).pipe(catchError(() => of(null)));
  }

  save(project: Project): Observable<Project | HttpErrorResponse> {
    if (project.id) {
      return this.http.put<Project>(`${this.environment.endPoint}project`, project).pipe(catchError(() => of(null)));
    }
    return this.http.post<Project>(`${this.environment.endPoint}project`, project).pipe(catchError((error: HttpErrorResponse) => of(error)));
  }

  duplicate(projectId: string, name: string): Observable<Project> {
    return this.http
      .post<Project>(`${this.environment.endPoint}project/duplicate`, {
        projectId,
        name
      })
      .pipe(catchError(() => of(null)));
  }

  rename(projectId: string, methodId: string, name: string): Observable<Project> {
    return this.http
      .put<Project>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/name`, {
        newName: name
      })
      .pipe(catchError(() => of(null)));
  }

  editBasicResearchDetails(projectId: string, researchMethodId: string, researchDetails: EditResearchDetails): Observable<boolean> {
    return this.http.put<Project>(`${this.environment.endPoint}project/${projectId}/research/${researchMethodId}/research/details`, researchDetails).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  saveAiInterviewQuestions(projectId: string, methodId: string, questions: AiInterviewQuestion[]): Observable<AiInterviewQuestion[]> {
    const url = `${this.environment.endPoint}project/${projectId}/research/${methodId}/ai-interview/questions`;

    return this.http.post<AiInterviewQuestion[]>(url, { questions: [...questions] }).pipe(catchError(() => of(null)));
  }

  saveNotepadJson(projectId: string, methodId: string, notepadJson: string): Observable<boolean> {
    return this.http.put<boolean>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/survey/notepad`, { notepadJson }).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  saveSurveyJson(projectId: string, methodId: string, surveyJson: string): Observable<boolean> {
    return this.http.put<boolean>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/survey/editor`, { surveyJson }).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  sendInvite(projectId: string, methodId: string, audienceId: string, panelMemberId: string): Observable<ProjectInvite> {
    return this.http
      .post<ProjectInvite>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/audience/${audienceId}/invite/${panelMemberId}`, {})
      .pipe(
        map((result) => result),
        catchError(() => of(null))
      );
  }

  mapAnswerResponses(answer: SurveyAnswer): ProjectResponse[] {
    const detail = answer.resolvedDetail;
    if (!detail) {
      throw new Error('Survey answer detail projection is unavailable');
    }
    const responses: ProjectResponse[] = [];
    const multiChoiceValues = [QuestionType.MultiChoice, QuestionType.HiddenChoice, QuestionType.RankSort, QuestionType.Ranking];
    for (const item of detail.responses) {
      const question = plainToClass(Question, item.question);
      const answerDetails = item.answer;
      let response: ProjectResponse;
      if (CarouselType.includes(question.type)) {
        response = this.mapCarouselValues(question, answerDetails);
      } else if (multiChoiceValues.includes(question.type)) {
        response = this.mapMultiChoiceValues(question, answerDetails);
      } else if (question.type === QuestionType.FileUpload) {
        response = this.mapFileUploadValues(question, answerDetails);
      } else if (question.type === QuestionType.AIChat) {
        response = this.mapAIChatValues(question, JSON.parse(answerDetails));
      } else if (question.type === QuestionType.Heatmap) {
        response = this.mapHeatmapValues(question, answerDetails);
      } else {
        response = this.mapDefaultValues(question, answerDetails);
      }
      if (response) {
        response.answerTime = item.duration == null ? null : GetTimeDifference(new Date(0), new Date(Math.round(item.duration) * 1000));
        response.stimuli = item.stimuli;
        response.loop = item.loop;
        responses.push(response);
      }
    }
    return responses;
  }

  mapAnswerSources(answer: SurveyAnswer): ProjectResponse[] {
    const detail = answer.resolvedDetail;
    if (!detail) {
      throw new Error('Survey answer detail projection is unavailable');
    }
    return detail.sourceVariables.map((variable) => ({
      questionTitle: variable.label,
      questionId: variable.id,
      questionCode: '',
      answer: variable.question
        ? this.mapDecodedAnswerResponseValue(plainToClass(Question, variable.question), variable.value)
        : [{ text: this.mapUnconfiguredAnswerValue(variable.value) }],
      answerTime: null
    }));
  }

  patchAiInterviewContent(projectId: string, methodId: string, content: AiInterviewContentRequest): Observable<ProjectAiInterviewContent> {
    const url = `${this.environment.endPoint}project/${projectId}/research/${methodId}/ai-interview/content`;

    return this.http.patch<ProjectAiInterviewContent>(url, { ...content });
  }

  rejectSurveyResponse(projectId: string, methodId: string, answerId: string): Observable<boolean> {
    return this.http.post<boolean>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/answers/${answerId}/reject`, {}).pipe(
      map(() => true),
      catchError((error) => of(error))
    );
  }

  removeInvitedUserFromResearch(projectId: string, methodId: string, userId: string): Observable<boolean> {
    return this.http
      .delete<boolean>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/user/${userId}/collaborators/user`)
      .pipe(catchError(() => of(false)));
  }

  updateResearchDetails(projectId: string, researchMethodId: string, researchDetails: ResearchDetails): Observable<boolean> {
    return this.http
      .put<boolean>(`${this.environment.endPoint}project/${projectId}/research/${researchMethodId}/v1/details`, researchDetails)
      .pipe(catchError(() => of(false)));
  }

  updateMainResearchDetails(projectId: string, researchMethodId: string, researchDetails: IEditMainResearchDetails): Observable<boolean> {
    return this.http
      .put<boolean>(`${this.environment.endPoint}project/${projectId}/research/${researchMethodId}/v2/details`, researchDetails)
      .pipe(catchError(() => of(false)));
  }

  updateResearchDescription(projectId: string, researchMethodId: string, description: string): Observable<boolean> {
    return this.http
      .put<boolean>(`${this.environment.endPoint}project/${projectId}/research/${researchMethodId}/description`, { newDescription: description })
      .pipe(catchError(() => of(false)));
  }

  getPrizeDrawEntries(projectId: string, researchMethodId: string): Observable<PrizeDrawEntry[]> {
    return this.http
      .get<PrizeDrawEntry[]>(`${this.environment.endPoint}project/${projectId}/research/${researchMethodId}/prize-draw/participants`)
      .pipe(catchError(() => of([])));
  }

  pickPrizeDrawWinner(projectId: string, researchMethodId: string): Observable<string> {
    return this.http
      .post<{ emailAddress: string }>(`${this.environment.endPoint}project/${projectId}/research/${researchMethodId}/prize-draw/winner`, null)
      .pipe(
        map((res) => res.emailAddress),
        catchError(() => of(null))
      );
  }

  getFollowUpQuestions(projectId: string, researchMethodId: string, prompt: AIPromptCore): Observable<IFollowUp> {
    prompt.turnId ??= createTurnId();
    const request = {
      answerText: prompt.answerText,
      questionId: prompt.questionId,
      turnId: prompt.turnId
    };

    return this.http.post<IFollowUp>(`${this.environment.surveyEndPoint}followup/${projectId}/${researchMethodId}`, request).pipe(catchError(() => of(null)));
  }

  getFeedback(projectId: string, researchMethodId: string): Observable<SurveyFeedback> {
    return this.http
      .get<SurveyFeedback>(`${this.environment.endPoint}project/${projectId}/research/${researchMethodId}/survey/feedback`)
      .pipe(catchError(() => of(null)));
  }

  validateProjectName(currentName: string = ''): AsyncValidatorFn {
    return (control: AbstractControl): Observable<any | null> => {
      if (!control.value || control.value === currentName) {
        return of(null);
      } else {
        return control.valueChanges.pipe(
          debounceTime(1000),
          first(),
          mergeMap((value: string) =>
            this.exists(value).pipe(
              map((hasUserNameBeenTaken: boolean) =>
                hasUserNameBeenTaken ? { unique: true, message: 'This title has already been used, please try another', isCustom: true } : null
              ),
              catchError(() => of(null))
            )
          )
        );
      }
    };
  }

  private mapAIGeneratedToEditor(selectedAiGeneratedQuestions: AIQuestion[]): SurveyModel {
    const surveyModel = new SurveyModel();
    surveyModel.pages = selectedAiGeneratedQuestions.map((question, index) => {
      const qMapped = new Question();
      qMapped.id = `Q${index + 1}`;
      qMapped.title = question.questionText;
      qMapped.type = question.questionType;
      if (question.options?.length > 0) {
        qMapped.options = question.options.map((opt, optIndex) => {
          const option = new QuestionOption();
          option.id = `opt_${optIndex + 1}`;
          option.value = `${optIndex + 1}`;
          option.text = opt;

          return option;
        });
      }

      const page = new PageModel();
      page.id = `page_${index}`;
      page.questions = [qMapped];

      return page;
    });

    return surveyModel;
  }

  private mapAIGeneratedToNotepad(selectedAiGeneratedQuestions: AIQuestion[]): NotepadModel {
    const notepad = new NotepadModel();
    notepad.questions = selectedAiGeneratedQuestions.map((question, index) => {
      const qMapped = new NotepadQuestion();
      qMapped.questionId = `${NotepadIdPrefix}${index + 1}`;
      qMapped.questionText = question.questionText;
      qMapped.questionType = question.questionType;
      qMapped.options = question.options;

      return qMapped;
    });

    return notepad;
  }

  private mapAnswerResponseValue(question: Question, result: unknown, cardId?: string): { text: string; stimulus?: Media }[] {
    let resultObject = result;

    if (typeof result === 'string') {
      resultObject = JSON.parse(result);
    }

    return this.mapDecodedAnswerResponseValue(question, resultObject, cardId);
  }

  private mapDecodedAnswerResponseValue(question: Question, resultObject: unknown, cardId?: string): { text: string; stimulus?: Media }[] {
    switch (question.type) {
      case QuestionType.Input:
      case QuestionType.Essay:
      case QuestionType.NPS: {
        return [{ text: resultObject as string }];
      }
      case QuestionType.HiddenChoice:
      case QuestionType.MultiChoice: {
        const typedResult = resultObject as ChoiceTypeResult;

        return (typedResult.value ? (typedResult.value as Array<string>) : []).map((value) => {
          if (value === 'OTHER') {
            return { text: `${question.otherLabel}: "${typedResult.otherText}"` };
          }
          const selectedOption = question.options.find((opt) => opt.value === value);
          return { text: selectedOption.text, stimulus: selectedOption.stimulus };
        });
      }
      case QuestionType.Dropdown:
      case QuestionType.SingleChoice: {
        const typedResult = resultObject as ChoiceTypeResult;
        if (!typedResult.value) {
          return [];
        }

        if (question.useOtherInd && typedResult.otherText?.length > 0) {
          return [{ text: `${question.otherLabel}: "${typedResult.otherText}"` }];
        }

        const selectedOption = question.options.find((opt) => opt.value === typedResult.value);
        return [{ text: selectedOption?.text, stimulus: selectedOption?.stimulus }];
      }
      case QuestionType.Carousel: {
        const carouselCardResult = resultObject[cardId] as ChoiceTypeResult;
        if (!carouselCardResult?.value) {
          return [];
        }

        if (question.useOtherInd && carouselCardResult.otherText?.length > 0) {
          return [{ text: `${question.otherLabel}: "${carouselCardResult.otherText}"` }];
        }

        const selectedOption = question.options.find((opt) => opt.value === carouselCardResult.value);
        return [{ text: selectedOption.text, stimulus: selectedOption.stimulus }];
      }
      case QuestionType.CarouselMultiChoice: {
        const carouselCardResult = resultObject[cardId] as ChoiceTypeResult;
        if (!carouselCardResult?.value) {
          return [];
        }

        return (carouselCardResult.value as Array<string>).map((value) => {
          if (value === 'OTHER') {
            return { text: `${question.otherLabel}: "${carouselCardResult.otherText}"` };
          }

          const selectedOption = question.options.find((opt) => opt.value === value);
          return { text: selectedOption.text, stimulus: selectedOption.stimulus };
        });
      }
      case QuestionType.CarouselRating: {
        const carouselCardResult = resultObject[cardId] as ChoiceTypeResult;
        if (!carouselCardResult?.value) {
          return [];
        }

        if (question.ratingType === RatingType.numeric || question.ratingType === RatingType.stars) {
          return [{ text: carouselCardResult.value as string }];
        } else if (question.ratingType === RatingType.descriptive) {
          const selectedOption = question.options.find((opt) => opt.value === carouselCardResult.value);
          return [{ text: selectedOption.text }];
        }
        break;
      }
      case QuestionType.Terminate: {
        return [{ text: question.title }];
      }
      case QuestionType.Ranking: {
        const typedResult = resultObject as Array<string>;
        if (!typedResult) {
          return [];
        }

        const selectedValues = [...typedResult]
          .sort((a, b) => +a - +b)
          .map((value) => {
            const selectedOption = question.options.find((opt) => opt.value === value);

            if (!selectedOption) {
              return { text: '' };
            }

            return { text: `${ToOrdinalNumber(+selectedOption.value)}: ${selectedOption.text}` };
          });

        return selectedValues;
      }
      case QuestionType.RankSort: {
        const typedResult = resultObject as ChoiceTypeResult;
        if (!typedResult) {
          return [];
        }

        return this.mapRankSortValues(typedResult, question);
      }
      case QuestionType.Rating: {
        const typedResult = resultObject as ChoiceTypeResult;
        if (question.ratingType === RatingType.numeric || question.ratingType === RatingType.stars) {
          return [{ text: `${typedResult.value}` }];
        }
        if (question.ratingType === RatingType.descriptive) {
          const selectedOption = question.options.find((opt) => opt.value === typedResult.value);
          return [{ text: selectedOption ? selectedOption.text : '' }];
        }
        return [];
      }
      default: {
        return [];
      }
    }
  }

  private mapCommentToServerComment(comment: Comment, referenceId: string): Comment {
    comment.referenceId = referenceId;
    comment.commentDateTime = new Date();
    return comment;
  }

  private mapUnconfiguredAnswerValue(value: unknown): string {
    return typeof value === 'string' ? value : JSON.stringify(value);
  }

  private mapToGenericComment(serverComment: Comment, comments: Comment[]): Comment {
    return { ...serverComment, replies: comments.filter((replies) => replies.replyToCommentId === serverComment.id) };
  }

  private mapDefaultValues(question: Question, answerDetails: string | ChoiceTypeResult | Media[]): ProjectResponse {
    const responseValue = this.mapAnswerResponseValue(question, answerDetails);

    if (responseValue.length > 0) {
      return {
        questionTitle: question.type === QuestionType.Terminate ? 'Terminate' : this.mapQuestionTitle(question),
        questionId: question.id,
        questionCode: question.code,
        answer: responseValue,
        answerTime: null
      };
    }
  }

  private mapFileUploadValues(question: Question, answerDetails: string | ChoiceTypeResult | Media[]): ProjectResponse {
    const mappedAnswers = JSON.parse(answerDetails as string) as Media[];
    const response = {
      questionTitle: `${question.id}. ${question.title}`,
      questionId: question.id,
      questionCode: question.code,
      answer: mappedAnswers.length <= 0 ? [{ text: 'No media uploaded' }] : [],
      media: mappedAnswers.map(
        (media) =>
          ({
            id: media.mediaId,
            name: media.name,
            type: media.type,
            url$: this.mediaService.getMediaUrl(media.mediaId).pipe(map((mediaResult) => mediaResult.url))
          }) as MediaView
      ),
      answerTime: null
    };
    return response;
  }

  private mapAIChatValues(question: Question, answerDetails: AIPrompt[]): ProjectResponse {
    const response = {
      questionTitle: `${question.id}. ${question.title}`,
      questionId: question.id,
      questionCode: question.code,
      answer: [{ text: answerDetails.length > 0 && !!answerDetails[0].answerText ? 'Conversation: ' : 'There were no responses given by the user' }],
      aiPrompts: answerDetails,
      answerTime: null
    };
    return response;
  }

  private mapHeatmapValues(question: Question, answerDetails: string): ProjectResponse {
    const heatmapPoints = this.parseHeatmapPoints(answerDetails);
    const coordinates = heatmapPoints.map((point) => ({ x: Math.round(+point.x), y: Math.round(+point.y), value: +point.value }));
    return {
      questionTitle: `${question.id}. ${question.title}`,
      questionId: question.id,
      questionCode: question.code,
      answer: [],
      heatmapQuestion: {
        imageHeatmap: { mediaId: question.imageHeatmap.mediaId },
        imageWidth: question.imageWidth,
        imageHeight: question.imageHeight,
        imageFit: question.imageFit,
        coordinates
      },
      answerTime: null
    };
  }

  private parseHeatmapPoints(answerDetails: string): { x: string | number; y: string | number; value: string | number }[] {
    const parsedAnswerDetails = answerDetails ? JSON.parse(answerDetails) : [];

    if (!parsedAnswerDetails || (typeof parsedAnswerDetails === 'string' && parsedAnswerDetails.trim().length === 0)) {
      return [];
    }

    return typeof parsedAnswerDetails === 'string' ? JSON.parse(parsedAnswerDetails) : parsedAnswerDetails;
  }

  private mapMultiChoiceValues(question: Question, answerDetails: string | ChoiceTypeResult | Media[]): ProjectResponse {
    const responseValue = this.mapAnswerResponseValue(question, answerDetails);
    const subResponses = [];

    if (responseValue.length > 0) {
      subResponses.push({
        questionTitle: '',
        questionId: '',
        answer: responseValue,
        answerTime: ''
      });
    }

    if (subResponses.length > 0) {
      return {
        questionTitle: this.mapQuestionTitle(question),
        questionId: question.id,
        questionCode: question.code,
        answer: [],
        answerTime: null,
        subResponses
      };
    }
  }

  private mapCarouselValues(question: Question, answerDetails: string | ChoiceTypeResult | Media[]): ProjectResponse {
    const subResponses = [];

    question.cards.forEach((card) => {
      const responseValue = this.mapAnswerResponseValue(question, answerDetails, card.id);

      if (responseValue.length > 0) {
        subResponses.push({
          questionTitle: card.text,
          answer: responseValue,
          answerTime: '',
          stimulus: card.stimulus
        });
      }
    });

    if (subResponses.length > 0) {
      return {
        questionTitle: this.mapQuestionTitle(question),
        questionId: question.id,
        questionCode: question.code,
        answer: [],
        answerTime: null,
        subResponses
      };
    }
  }

  private mapRankSortValues(typedResult: ChoiceTypeResult, question: Question): { text: string; stimulus?: Media }[] {
    if (!typedResult.value) {
      return [];
    }

    if (typedResult.value.includes('NONE')) {
      return [{ text: question.noneLabel }];
    }

    const selectedValues = (typedResult.value as string[]).map((value, index) => {
      let selectedOptionText = '';
      if (value) {
        selectedOptionText = value === 'OTHER' ? `Other: ${typedResult.otherText}` : question.options.find((opt) => opt.value === value)?.text;
      }

      const rankText = question.ratingType === RatingType.numeric ? ToOrdinalNumber(index + 1) : question.ranks[index].text;

      return { text: `${rankText}: ${selectedOptionText}` };
    });

    return selectedValues;
  }

  private mapQuestionTitle(question: Question): string {
    return question.title || question.variableToken;
  }
}
